import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Banner,
  Button,
  Card,
  Combobox,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { useCatalogItemPicker } from '../catalog/useCatalogItemPicker';
import { suppliersApi } from '../suppliers/suppliersApi';
import { settingsApi } from '../settings/settingsApi';
import { purchaseOrderPath } from '../../lib/slug';
import { purchaseOrdersApi } from './purchaseOrdersApi';
import BudgetPanel from '../budgets/BudgetPanel';
import '../requisitions/requisitions.css';
import './purchase-orders.css';

let tmpId = 0;
const newLine = (over = {}) => ({
  key: `tmp-${++tmpId}`,
  itemId: '',
  description: '',
  sku: '',
  quantity: '',
  uomId: '',
  unitPrice: '',
  discountPercent: '',
  taxPercent: '',
  costCenter: '',
  glAccount: '',
  notes: '',
  ...over,
});

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

const isBlankLine = (l) => !l.itemId && !l.description.trim();

export default function PurchaseOrderFormPage() {
  const { id: routeRef } = useParams();
  const editing = Boolean(routeRef);
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { activePropertyId } = useProperty();

  // Resolved real id of the order being edited (the URL carries the serial number).
  const [editingId, setEditingId] = useState(null);
  const [wasPending, setWasPending] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [submitting, setSubmitting] = useState(false);

  const itemPicker = useCatalogItemPicker({ propertyId: activePropertyId });
  const items = itemPicker.items;
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currencies, setCurrencies] = useState(['USD']);
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  // Cache of item -> linked suppliers, used to autofill prices from the header supplier's price list.
  const itemSuppliersRef = useRef({});

  const [title, setTitle] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [expected, setExpected] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('');
  const [freight, setFreight] = useState('');
  const [otherCharges, setOtherCharges] = useState('');
  const [lines, setLines] = useState([newLine()]);

  const loadLookups = useCallback(async () => {
    try {
      const [us, locs, sups, eff] = await Promise.all([
        catalogApi.listUnits(),
        catalogApi.listLocations(activePropertyId),
        suppliersApi.list({ pageSize: 500 }),
        settingsApi.getEffective(activePropertyId).catch(() => null),
      ]);
      setUnits(us || []);
      setLocations(locs || []);
      setSuppliers(sups?.items || []);
      const ccys = eff?.currencies?.length ? eff.currencies : ['USD'];
      setCurrencies(ccys);
      setCostCenters((eff?.costCenters || []).filter((c) => c.isActive !== false));
      setGlAccounts((eff?.glAccounts || []).filter((g) => g.isActive !== false));
      if (!editing) setCurrency(eff?.defaultCurrency || ccys[0] || 'USD');
    } catch {
      // Non-fatal; the form still works with free-text lines and USD.
    }
  }, [activePropertyId, editing]);

  const loadOrder = useCallback(async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const p = await purchaseOrdersApi.get(routeRef);
      setEditingId(p.id);
      if (p.status !== 'Draft' && p.status !== 'PendingApproval') {
        toast.error(t('Approved orders are final and can no longer be edited.'));
        navigate(purchaseOrderPath(p));
        return;
      }
      setWasPending(p.status === 'PendingApproval');
      setTitle(p.title || '');
      setSupplierId(p.supplierId || '');
      setLocationId(p.locationId || '');
      setExpected(p.expectedDate ? p.expectedDate.slice(0, 10) : '');
      setCurrency(p.currency || 'USD');
      setNotes(p.notes || '');
      setFreight(p.freightAmount?.amount ? String(p.freightAmount.amount) : '');
      setOtherCharges(p.otherCharges?.amount ? String(p.otherCharges.amount) : '');
      setLines([
        ...(p.lines || []).map((l) => newLine({
          itemId: l.itemId || '',
          description: l.description || '',
          sku: l.sku || '',
          quantity: l.quantity ?? '',
          uomId: l.uomId || '',
          unitPrice: l.unitPrice?.amount ?? '',
          discountPercent: l.discountPercent ? String(l.discountPercent) : '',
          taxPercent: l.taxPercent ? String(l.taxPercent) : '',
          costCenter: l.costCenter || '',
          glAccount: l.glAccountCode || '',
          notes: l.notes || '',
        })),
        newLine(),
      ]);
    } catch (err) {
      toast.error(t('Could not load purchase order'), { description: err?.message });
      navigate('/purchase-orders');
    } finally {
      setLoading(false);
    }
  }, [editing, routeRef, navigate, toast, t]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { loadOrder(); }, [loadOrder]);

  // Pull in items an existing order references but the first catalog page didn't include.
  useEffect(() => {
    itemPicker.ensureIds([...new Set(lines.map((l) => l.itemId).filter(Boolean))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  const loadItemSuppliers = useCallback(async (itemId) => {
    if (!itemId) return [];
    if (itemSuppliersRef.current[itemId]) return itemSuppliersRef.current[itemId];
    let list = [];
    try { list = (await catalogApi.listItemSuppliers(itemId)) || []; } catch { list = []; }
    itemSuppliersRef.current = { ...itemSuppliersRef.current, [itemId]: list };
    return list;
  }, []);

  // The supplier's price-list entry for an item, if any.
  const supplierLink = (itemId, sid) =>
    (itemSuppliersRef.current[itemId] || []).find((s) => s.supplierId === sid && s.isActive);

  const itemOptions = itemPicker.options;
  const uomOptions = useMemo(
    () => [{ value: '', label: '—' }, ...units.map((u) => ({ value: u.id, label: u.code || u.name }))],
    [units],
  );
  const locationOptions = useMemo(
    () => [{ value: '', label: t('— No location —') }, ...locations.map((l) => ({ value: l.id, label: l.name }))],
    [locations, t],
  );
  const supplierOptions = useMemo(
    () => [{ value: '', label: t('— Select supplier —') }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))],
    [suppliers, t],
  );
  const currencyOptions = useMemo(() => currencies.map((c) => ({ value: c, label: c })), [currencies]);
  // Per-line coding option lists.
  const lineCostCenterOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...costCenters.map((c) => ({ value: c.code, label: c.name ? `${c.code} · ${c.name}` : c.code }))],
    [costCenters, t],
  );
  const lineGlAccountOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...glAccounts.map((g) => ({ value: g.code, label: g.name ? `${g.code} · ${g.name}` : g.code }))],
    [glAccounts, t],
  );

  const withTrailingBlank = (next) => {
    const trimmed = [...next];
    while (trimmed.length > 1 && isBlankLine(trimmed[trimmed.length - 1]) && isBlankLine(trimmed[trimmed.length - 2])) {
      trimmed.pop();
    }
    if (trimmed.length === 0 || !isBlankLine(trimmed[trimmed.length - 1])) trimmed.push(newLine());
    return trimmed;
  };

  const updateLine = (key, patch) =>
    setLines((prev) => withTrailingBlank(prev.map((l) => (l.key === key ? { ...l, ...patch } : l))));

  const selectItem = (key, itemId) => {
    const item = items.find((i) => i.id === itemId);
    setLines((prev) => withTrailingBlank(prev.map((l) => {
      if (l.key !== key) return l;
      if (!item) return { ...l, itemId: '' };
      return {
        ...l,
        itemId: item.id,
        description: l.description?.trim() ? l.description : item.name,
        sku: item.sku || '',
        uomId: l.uomId || item.stockUomId || '',
        quantity: l.quantity === '' ? 1 : l.quantity,
        unitPrice: l.unitPrice === '' ? (item.standardCost?.amount ?? '') : l.unitPrice,
        costCenter: l.costCenter || item.defaultCostCenter || '',
        glAccount: l.glAccount || item.defaultGlAccountCode || '',
      };
    })));
    // Pull the price from the chosen supplier's price list once it loads.
    if (item) {
      loadItemSuppliers(item.id).then(() => {
        const src = supplierLink(item.id, supplierId);
        if (!src) return;
        const stdCost = item.standardCost?.amount;
        setLines((prev) => withTrailingBlank(prev.map((l) => {
          if (l.key !== key || l.itemId !== item.id) return l;
          const overridable = l.unitPrice === '' || (stdCost != null && Number(l.unitPrice) === stdCost);
          return {
            ...l,
            uomId: l.uomId || src.purchaseUomId || l.uomId,
            unitPrice: overridable && src.price ? src.price.amount : l.unitPrice,
          };
        })));
      });
    }
  };

  // Switching the header supplier refills empty line prices from the new supplier's price list.
  const changeSupplier = (sid) => {
    setSupplierId(sid);
    if (!sid) return;
    const itemIds = [...new Set(lines.map((l) => l.itemId).filter(Boolean))];
    Promise.all(itemIds.map((iid) => loadItemSuppliers(iid))).then(() => {
      setLines((prev) => withTrailingBlank(prev.map((l) => {
        if (!l.itemId) return l;
        const src = supplierLink(l.itemId, sid);
        if (!src) return l;
        const stdCost = items.find((i) => i.id === l.itemId)?.standardCost?.amount;
        const overridable = l.unitPrice === '' || (stdCost != null && Number(l.unitPrice) === stdCost);
        return {
          ...l,
          uomId: l.uomId || src.purchaseUomId || l.uomId,
          unitPrice: overridable && src.price ? src.price.amount : l.unitPrice,
        };
      })));
    });
  };

  const removeLine = (key) =>
    setLines((prev) => withTrailingBlank(prev.filter((l) => l.key !== key)));

  const addBlankLine = () => setLines((prev) => [...prev, newLine()]);

  // Net extended amount for a line: qty × price, less the line discount, excluding tax.
  const lineNet = (l) => {
    const p = parseFloat(l.unitPrice);
    const q = parseFloat(l.quantity);
    if (Number.isNaN(p) || Number.isNaN(q)) return null;
    const disc = Math.min(Math.max(num(l.discountPercent), 0), 100);
    return p * q * (1 - disc / 100);
  };
  const realLines = lines.filter((l) => !isBlankLine(l));
  const totals = useMemo(() => {
    let subtotal = 0;
    let discount = 0;
    let tax = 0;
    for (const l of realLines) {
      const p = parseFloat(l.unitPrice);
      const q = parseFloat(l.quantity);
      if (Number.isNaN(p) || Number.isNaN(q)) continue;
      const gross = p * q;
      const d = gross * Math.min(Math.max(num(l.discountPercent), 0), 100) / 100;
      const net = gross - d;
      subtotal += gross;
      discount += d;
      tax += net * Math.min(Math.max(num(l.taxPercent), 0), 100) / 100;
    }
    const freightAmt = Math.max(0, num(freight));
    const otherAmt = Math.max(0, num(otherCharges));
    return {
      subtotal,
      discount,
      tax,
      freight: freightAmt,
      other: otherAmt,
      grand: subtotal - discount + tax + freightAmt + otherAmt,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, freight, otherCharges]);
  const money = (n) => `${currency} ${formatAmount(n)}`;

  // Budget projection groups: each line attributed to its own coding.
  const codingGroups = realLines
    .map((l) => ({
      costCenter: l.costCenter,
      glAccountCode: l.glAccount,
      amount: lineNet(l) || 0,
    }))
    .filter((g) => g.costCenter);

  const buildPayload = () => ({
    title: title.trim(),
    supplierId: supplierId || null,
    locationId: locationId || null,
    expectedDate: expected ? new Date(expected).toISOString() : null,
    currency: (currency || 'USD').toUpperCase(),
    notes: notes.trim() || null,
    freightAmount: freight === '' ? null : Number(freight),
    otherCharges: otherCharges === '' ? null : Number(otherCharges),
    lines: realLines.map((l) => ({
      itemId: l.itemId || null,
      description: l.description.trim(),
      sku: l.sku?.trim() || null,
      quantity: Number(l.quantity),
      uomId: l.uomId || null,
      unitPrice: l.unitPrice === '' ? null : Number(l.unitPrice),
      discountPercent: l.discountPercent === '' ? null : Number(l.discountPercent),
      taxPercent: l.taxPercent === '' ? null : Number(l.taxPercent),
      glAccountCode: l.glAccount.trim() || null,
      costCenter: l.costCenter.trim() || null,
      notes: l.notes?.trim() || null,
    })),
  });

  const validate = () => {
    if (!title.trim()) { toast.error(t('Enter a title.')); return false; }
    for (const l of realLines) {
      if (!l.description.trim()) { toast.error(t('Every line needs a description.')); return false; }
      if (!(Number(l.quantity) > 0)) { toast.error(t('"{line}" needs a quantity greater than 0.', { line: l.description || t('A line') })); return false; }
    }
    return true;
  };

  const save = async (submitAfter) => {
    if (!validate()) return;
    if (submitAfter && !supplierId) { toast.error(t('Assign a supplier before submitting.')); return; }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const saved = editing ? await purchaseOrdersApi.update(editingId, { ...payload, id: editingId }) : await purchaseOrdersApi.create(payload);
      if (submitAfter) {
        const submitted = await purchaseOrdersApi.submit(saved.id);
        toast.success(t('{number} submitted for approval', { number: saved.number }));
        if (submitted?.budgetWarning) {
          toast.warning(t('Submitted over budget'), { description: submitted.budgetWarning });
        }
      } else {
        toast.success(editing ? t('Purchase order updated') : t('{number} created', { number: saved.number }));
      }
      navigate(purchaseOrderPath(saved));
    } catch (err) {
      toast.error(t('Could not save purchase order'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/purchase-orders')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Purchase Orders')}
      </Button>

      <PageHeader
        title={editing ? t('Edit purchase order') : t('New purchase order')}
        description={t('Commit an order to a supplier. Submit it to route for approval.')}
        icon={<Icon.ShoppingCart size={22} />}
      />

      {wasPending && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">
            {t('This order is awaiting approval. Saving your changes will withdraw it from approval — the approval chain resets and it must be resubmitted.')}
          </Banner>
        </div>
      )}

      <div className="req-form">
        <Card>
          <Card.Body>
            <div className="req-form">
              <Input label={t('Title')} placeholder={t('e.g. Weekly produce order')} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
              <div className="req-form__grid">
                <div className="ps-field">
                  <span className="ps-field__label">{t('Supplier')}</span>
                  <Select value={supplierId} onChange={changeSupplier} options={supplierOptions} ariaLabel={t('Supplier')} searchable />
                </div>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Deliver-to location')}</span>
                  <Select value={locationId} onChange={setLocationId} options={locationOptions} ariaLabel={t('Location')} searchable />
                </div>
                <Input label={t('Expected date')} type="date" value={expected} onChange={(e) => setExpected(e.target.value)} />
                <div className="ps-field">
                  <span className="ps-field__label">{t('Currency')}</span>
                  <Select value={currency} onChange={setCurrency} options={currencyOptions} ariaLabel={t('Currency')} />
                </div>
              </div>
              <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Delivery instructions or context (optional)')} />
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header><Card.Title>{t('Line items')}</Card.Title></Card.Header>
          <Card.Body>
            <div className="req-doc">
              <div className="req-doc__head po-grid">
                <span className="req-doc__col-num">#</span>
                <span>{t('Item')}</span>
                <span>{t('Description')}</span>
                <span className="req-doc__num">{t('Qty')}</span>
                <span>{t('Unit')}</span>
                <span className="req-doc__num">{t('Unit price')}</span>
                <span className="req-doc__num">{t('Disc %')}</span>
                <span className="req-doc__num">{t('Tax %')}</span>
                <span className="req-doc__num">{t('Amount')}</span>
                <span />
              </div>

              {lines.map((l, idx) => {
                const total = lineNet(l);
                const blank = isBlankLine(l);
                return (
                  <div className={`req-doc__line ${blank ? 'req-doc__line--blank' : ''}`} key={l.key}>
                  <div className={`req-doc__row po-grid ${blank ? 'req-doc__row--blank' : ''}`}>
                    <span className="req-doc__col-num">{blank ? '' : idx + 1}</span>
                    <div className="req-doc__cell">
                      <Combobox
                        options={itemOptions}
                        value={l.itemId}
                        onChange={(v) => selectItem(l.key, v)}
                        onSearch={itemPicker.onSearchChange}
                        placeholder={t('Search catalog…')}
                        searchPlaceholder={t('Search name or SKU…')}
                        size="sm"
                        emptyText={itemPicker.searching ? t('Searching…') : t('No catalog matches')}
                        ariaLabel={t('Catalog item')}
                      />
                    </div>
                    <div className="req-doc__cell">
                      <Input size="sm" value={l.description} placeholder={t('Description')} onChange={(e) => updateLine(l.key, { description: e.target.value })} />
                    </div>
                    <div className="req-doc__cell req-doc__num">
                      <Input size="sm" type="number" value={l.quantity} placeholder="0" onChange={(e) => updateLine(l.key, { quantity: e.target.value })} />
                    </div>
                    <div className="req-doc__cell">
                      <Select size="sm" value={l.uomId} onChange={(v) => updateLine(l.key, { uomId: v })} options={uomOptions} ariaLabel={t('Unit')} searchable />
                    </div>
                    <div className="req-doc__cell req-doc__num">
                      <Input size="sm" type="number" value={l.unitPrice} placeholder="0.00" onChange={(e) => updateLine(l.key, { unitPrice: e.target.value })} />
                    </div>
                    <div className="req-doc__cell req-doc__num">
                      <Input size="sm" type="number" value={l.discountPercent} placeholder="0" onChange={(e) => updateLine(l.key, { discountPercent: e.target.value })} />
                    </div>
                    <div className="req-doc__cell req-doc__num">
                      <Input size="sm" type="number" value={l.taxPercent} placeholder="0" onChange={(e) => updateLine(l.key, { taxPercent: e.target.value })} />
                    </div>
                    <span className="req-doc__amount">{total != null ? money(total) : <span className="pp-cell-muted">—</span>}</span>
                    <span className="req-doc__cell">
                      {!blank && (
                        <IconButton aria-label={t('Remove line')} variant="ghost" size="icon-sm" onClick={() => removeLine(l.key)}><Icon.Trash size={15} /></IconButton>
                      )}
                    </span>
                  </div>
                  {!blank && (costCenters.length > 0 || glAccounts.length > 0) && (
                    <div className="req-doc__coding">
                      <span className="req-doc__coding-label">{t('Coding')}</span>
                      <div className="req-doc__coding-field">
                        <Select size="sm" value={l.costCenter} onChange={(v) => updateLine(l.key, { costCenter: v })} options={lineCostCenterOptions} ariaLabel={t('Line cost centre')} searchable />
                      </div>
                      <div className="req-doc__coding-field">
                        <Select size="sm" value={l.glAccount} onChange={(v) => updateLine(l.key, { glAccount: v })} options={lineGlAccountOptions} ariaLabel={t('Line GL account')} searchable />
                      </div>
                    </div>
                  )}
                  </div>
                );
              })}

              <div className="req-doc__foot">
                <Button variant="ghost" size="sm" leadingIcon={<Icon.Plus size={14} />} onClick={addBlankLine}>{t('Add blank line')}</Button>
                <div className="po-totals">
                  <div className="po-totals__row">
                    <span>{t('Subtotal')}</span>
                    <span className="po-totals__row-value">{money(totals.subtotal)}</span>
                  </div>
                  {totals.discount > 0 && (
                    <div className="po-totals__row">
                      <span>{t('Discount')}</span>
                      <span className="po-totals__row-value">−{money(totals.discount)}</span>
                    </div>
                  )}
                  <div className="po-totals__row">
                    <span>{t('Tax')}</span>
                    <span className="po-totals__row-value">{money(totals.tax)}</span>
                  </div>
                  <div className="po-totals__row po-totals__row--charge">
                    <span>{t('Freight')}</span>
                    <Input wrapClassName="po-totals__charge-input" size="sm" type="number" value={freight} placeholder="0.00" onChange={(e) => setFreight(e.target.value)} aria-label={t('Freight amount')} />
                  </div>
                  <div className="po-totals__row po-totals__row--charge">
                    <span>{t('Other charges')}</span>
                    <Input wrapClassName="po-totals__charge-input" size="sm" type="number" value={otherCharges} placeholder="0.00" onChange={(e) => setOtherCharges(e.target.value)} aria-label={t('Other charges')} />
                  </div>
                  <div className="po-totals__divider" />
                  <div className="po-totals__row po-totals__row--grand">
                    <span>{t('Order total')}</span>
                    <span className="po-totals__row-value">{money(totals.grand)}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {codingGroups.length > 0 && (
          <BudgetPanel propertyId={activePropertyId} groups={codingGroups} currency={currency} />
        )}

        <div className="req-form__actions">
          <Button variant="ghost" onClick={() => navigate('/purchase-orders')} disabled={submitting}>{t('Cancel')}</Button>
          <Button variant="secondary" loading={submitting} onClick={() => save(false)}>{t('Save draft')}</Button>
          <Button variant="primary" loading={submitting} leadingIcon={<Icon.ArrowUpCircle size={15} />} onClick={() => save(true)}>{t('Save & submit')}</Button>
        </div>
      </div>
    </PageContainer>
  );
}
